import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../utils/axiosConfig";
import { HearingStatus, PaymentStatus, HearingAgentStatus } from "../utils/enums/hearing.enums";

interface HearingHistory {
  h_id: number;
  h_processNumber: string;
  hearingStatus: HearingStatus;
  h_hearingDate: string;
  hp_scheduledFor: string;
  fee: string;
  company: string;
  paymentStatus: keyof typeof PaymentStatus;
  agentStatus?: number;
}

type Counters = Record<HearingStatus, number>;

const initialCounters: Counters = {
  [HearingStatus.Agendada]: 0,
  [HearingStatus.Confirmada]: 0,
  [HearingStatus.Realizada]: 0,
  [HearingStatus.Finalizada]: 0,
  [HearingStatus.Cancelada]: 0,
};

const statusConfig: Record<HearingStatus, { label: string; dotClass: string }> = {
  [HearingStatus.Agendada]: { label: "Agendadas", dotClass: "bg-blue-400" },
  [HearingStatus.Confirmada]: { label: "Confirmadas", dotClass: "bg-indigo-400" },
  [HearingStatus.Realizada]: {
    label: "Realizadas (pend. relatório)",
    dotClass: "bg-amber-400",
  },
  [HearingStatus.Finalizada]: { label: "Finalizadas", dotClass: "bg-emerald-400" },
  [HearingStatus.Cancelada]: { label: "Canceladas", dotClass: "bg-red-400" },
};

const HearingsCounter: React.FC = () => {
  const [history, setHistory] = useState<HearingHistory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await axiosInstance.get<HearingHistory[]>(
          "hearings/agent/history"
        );
        console.log("HearingsCounter - Raw API response:", response.data);
        setHistory(response.data || []);
      } catch (e) {
        console.error("Erro ao carregar contadores de audiências", e);
        setError("Não foi possível carregar os contadores.");
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, []);

  const counters = useMemo(() => {
    const acc: Counters = { ...initialCounters };
    console.log("HearingsCounter - All hearings:", history);
    
    // Filter out hearings with pending invitations (agentStatus = 1)
    const filteredHistory = history.filter((h) => {
      console.log(`Hearing ${h.h_id}: agentStatus = ${h.agentStatus}, type = ${typeof h.agentStatus}`);
      return h.agentStatus !== HearingAgentStatus.Convidado && h.agentStatus !== 1;
    });
    console.log("HearingsCounter - Filtered hearings (excluding pending):", filteredHistory);
    console.log("HearingAgentStatus.Convidado value:", HearingAgentStatus.Convidado);
    
    for (const h of filteredHistory) {
      if (h?.hearingStatus && acc[h.hearingStatus] !== undefined) {
        acc[h.hearingStatus] += 1;
      }
    }
    console.log("HearingsCounter - Final counters:", acc);
    return acc;
  }, [history]);

  if (loading) {
    return (
      <div className="container mx-auto p-2 md:p-3 lg:p-4 mt-0 mb-0">
        <div className="bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden">
          <div className="grid grid-cols-2 xl:grid-cols-4 divide-y xl:divide-y-0 divide-x divide-slate-100">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="px-4 py-3 sm:py-4">
                <div className="h-3 w-20 bg-gray-100 rounded animate-pulse" />
                <div className="h-6 w-10 bg-gray-200 rounded mt-2 animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-2 md:p-3 lg:p-4 mt-0 mb-0">
        <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm ring-1 ring-red-200">
          {error}
        </div>
      </div>
    );
  }

  const statusOrder = (Object.keys(initialCounters) as HearingStatus[]).filter(
    (status) => status !== HearingStatus.Confirmada,
  );

  return (
    <div className="container mx-auto p-2 md:p-3 lg:p-4 mt-0 mb-0">
      <div className="bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden">
        <div className="grid grid-cols-2 xl:grid-cols-4 divide-x divide-y xl:divide-y-0 divide-slate-100">
          {statusOrder.map((status) => {
            const config = statusConfig[status];

            return (
              <div key={status} className="px-4 py-3 sm:py-4 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    aria-hidden
                    className={`h-2 w-2 rounded-full shrink-0 ${config.dotClass}`}
                  />
                  <span className="text-xs md:text-sm font-medium text-slate-600 truncate">
                    {config.label}
                  </span>
                </div>
                <p className="mt-1.5 text-xl md:text-2xl font-bold tracking-tight text-preppoBlue tabular-nums">
                  {counters[status] ?? 0}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default HearingsCounter;